namespace $.$$ {

	export type $shm_hitalama_map_widget_drag_page = {
		layout_drag_plan(): $shm_hitalama_map_page_layout_drop_plan
		layout_drag_commit( force?: boolean, plan?: $shm_hitalama_map_page_layout_drop_plan ): unknown
	}
	export type $shm_hitalama_map_widget_drag_gesture = {
		event?: PointerEvent
		committed: boolean
		ended: WeakSet< $.$rise_drag >
		plan?: $shm_hitalama_map_page_layout_drop_plan
	}
	const $shm_hitalama_map_widget_drag_finishing = new WeakSet< object >()
	const $shm_hitalama_map_widget_drag_pointerup_version = 6

	export function $shm_hitalama_map_widget_drag_is_finishing( page: object ) {
		return $shm_hitalama_map_widget_drag_finishing.has( page )
	}

	export function $shm_hitalama_map_widget_drag_finish(
		page: $shm_hitalama_map_widget_drag_page,
		primary: $.$rise_drag,
		synced: readonly $.$rise_drag[],
		event: PointerEvent,
		gesture: $shm_hitalama_map_widget_drag_gesture,
	) {
		if( gesture.event !== event ) {
			gesture.event = event
			gesture.committed = false
			gesture.ended = new WeakSet
			gesture.plan = undefined
		}
		$shm_hitalama_map_widget_drag_finishing.add( page )
		try {
			// Commit while the preview and dragged geometry still describe the drop.
			// `dragged(false)` is deliberately the last reactive change so the final
			// render reads the already committed order and positions.
			if( !gesture.committed ) {
				gesture.plan ??= page.layout_drag_plan()
				page.layout_drag_commit( true, gesture.plan )
				gesture.committed = true
			}
			for( const drag of [ primary, ... synced ] ) {
				if( gesture.ended.has( drag ) ) continue
				drag.drag_end( event )
				gesture.ended.add( drag )
			}
		} finally {
			$shm_hitalama_map_widget_drag_finishing.delete( page )
		}
	}

	export class $shm_hitalama_map_widget_float extends $.$shm_hitalama_map_widget_float {

		@ $mol_mem
		override dom_tree( next?: Element ) {
			const loading = this.$.$shm_hitalama_map_loading.current( this.Map_page().map().link().str )
			const id = this.dom_id()
			loading?.render_start( id, this )
			const node = super.dom_tree( next )
			if( loading?.root.pending() ) {
				// Position/style rendering has already read Render_data. No separate land is loaded.
				const label = this.$.$mol_error_fence( ()=> this.widget().title(), ()=> this.widget().link_str(), ()=> this.widget().link_str() )
				loading.render_end( id, node, label )
			}
			return node
		}

		private drag_pointerup_prepare( drag: $.$rise_drag, force = false ) {
			const sync = drag as typeof drag & {
				handle_pointerup_async: ( event: PointerEvent )=> unknown
				handle_pointermove_async: ( event: PointerEvent )=> unknown
				drags_synced_frozen: readonly $.$rise_drag[]
				start_event?: PointerEvent
				hitalama_pointerup_version?: number
			}
			if( !force && sync.hitalama_pointerup_version === $shm_hitalama_map_widget_drag_pointerup_version ) return drag

			sync.hitalama_pointerup_version = $shm_hitalama_map_widget_drag_pointerup_version
			const gesture: $shm_hitalama_map_widget_drag_gesture = { committed: false, ended: new WeakSet }
			let released = false
			let moving = false
			const move_allowed = ( event: PointerEvent )=> {
				const start = sync.start_event
				if( !start || start.pointerType !== 'touch' ) return true
				if( moving ) return true
				return moving = Math.hypot( event.clientX - start.clientX, event.clientY - start.clientY ) >= 8
			}
			sync.handle_pointermove_async = $mol_wire_async( ( event: PointerEvent )=> {
				// Check on every wire replay too: an earlier move can still be pending.
				if( released || !sync.dragged() ) return
				if( sync.start_event && sync.start_event.pointerId !== event.pointerId ) return
				if( !move_allowed( event ) ) return
				sync.drag( event )
				for( const other of sync.drags_synced_frozen ) other.drag( event )
			} )
			const finish = $mol_wire_async( ( event: PointerEvent )=> {
				// The final pointermove can still be suspended when pointerup arrives.
				// Read the release position through stable child tasks before planning
				// the drop; completed tasks are reused if commit/history suspends.
				if( move_allowed( event ) ) {
					$mol_wire_sync( sync ).drag( event )
					for( const other of sync.drags_synced_frozen ) $mol_wire_sync( other ).drag( event )
				} else {
					// A tap must not snap geometry or commit a layout drop on release.
					gesture.event = event
					gesture.committed = true
				}
				// Commit before ending the drag. The final `dragged(false)` then invalidates
				// the render against the already stored order and geometry.
				$shm_hitalama_map_widget_drag_finish(
					this.Map_page() as unknown as $shm_hitalama_map_widget_drag_page,
					sync,
					sync.drags_synced_frozen,
					event,
					gesture,
				)
				sync.drags_synced_frozen = []
				sync.pointerup_listener()
				sync.pointermove_listener()
			} )
			sync.handle_pointerup_async = event => {
				// A cancelled drag can leave an already queued pointerup callback.
				if( !sync.dragged() ) return
				if( sync.start_event && sync.start_event.pointerId !== event.pointerId ) return
				// Stop accepting pointer moves before commit/history can suspend.
				released = true
				return finish( event )
			}
			return drag
		}

		Drag_view() {
			const view = super.Drag_view()
			this.drag_pointerup_prepare( view.Drag() )

			return view
		}

		drag_pointerdown( event: PointerEvent ) {
			if( this.map().interaction_locked() ) return
			if( !this.map().can_change() ) return
			// `Drag_view` survives hot reload. Refresh the actual drag handler at the
			// gesture boundary so an already opened map cannot keep the old pointerup.
			this.drag_pointerup_prepare( this.Drag_view().Drag(), true )
			return super.drag_pointerdown( event )
		}

		head_view() {
			if( this.hide_head_tools() ) return []
			return this.head_showed() ? super.head_view() : []
		}

		sub(): readonly ( any )[] {
			return this.scrollable() ? this.sub_scrollable() : super.sub()
		}

		map(): $shm_hitalama_map {
			return this.Map_page().map()
		}

		on_drag_end( event?: PointerEvent | null ) {
			const page = this.Map_page() as unknown as $shm_hitalama_map_widget_drag_page
			if( !$shm_hitalama_map_widget_drag_is_finishing( page ) ) page.layout_drag_commit()
			$shm_hitalama_map_history.geometry_end( this.widget() )
			return event ?? null
		}

		on_drag( event?: PointerEvent | null ) {
			this.Map_page().layout_drag_preview()
			return event ?? null
		}

		override resize_start( next?: any ) {
			if( this.map().interaction_locked() ) return null
			$shm_hitalama_map_history.geometry_begin_many([ this.widget() ])
			return super.resize_start( next )
		}

		@ $mol_mem
		selected( next?: boolean ): boolean {
			return this.Map_page().selected( this.widget().link(), next )
		}

		@ $mol_mem
		hovered( next?: boolean ): boolean {
			return this.Map_page().hovered( this.widget().link(), next )
		}

		stickable(): boolean {
			return this.Map_page().stickable()
		}

		sticks_x(): readonly number[] {
			return this.Map_page().sticks_x( this.widget().link() )
		}

		sticks_y(): readonly number[] {
			return this.Map_page().sticks_y( this.widget().link() )
		}

		drags_synced(): readonly ( $.$rise_drag )[] {
			return this.Map_page().drags_synced( this.widget().link() )
		}

		on_contextmenu( next?: PointerEvent | null ) {
			if( next == null ) return null
			return this.Map_page().widget_event_contextmenu( this.widget().link(), next )
		}

		head_showed(): boolean {
			return this.Map_page().widget_headers_visible()
		}

		toolbar_type(): string {
			return `(${this.widget().type()})`
		}
		
		@ $mol_action
		delete_from_map() {
			this.map().widget_cut( this.widget().link() )
		}

		@ $mol_mem
		y( next?: number ): number {
			if( this.locked() ) return this.$.$mol_mem_cached(()=>this.y()) ?? next!
			if( next === undefined ) return this.top()

			const top = this.drag_to_stick( this.sticks_y(), next, 0 )
			const snapped = top === next
				? this.drag_to_stick( this.sticks_y(), next, this.height() )
				: top
			return this.top( snapped )
		}

		@ $mol_mem
		x( next?: number ): number {
			if( this.locked() ) return this.$.$mol_mem_cached(()=>this.x()) ?? next!
			if( next === undefined ) return this.left()

			const left = this.drag_to_stick( this.sticks_x(), next, 0 )
			const snapped = left === next
				? this.drag_to_stick( this.sticks_x(), next, this.width() )
				: left
			return this.left( snapped )
		}

		drag_to_stick( sticks: readonly number[], value: number, shift: number ) {
			if( !this.stickable() ) return value
			for( const stick of sticks ) {
				const delta = stick - ( value + shift )
				if( Math.abs( delta ) < 10 ) return value + delta
			}
			return value
		}

		@ $mol_mem
		x_stick( next?: number ) {
			return this.widget().body_x(next)
		}

		@ $mol_mem
		y_stick( next?: number ) {
			return this.widget().body_y(next)
		}

		@ $mol_mem
		bottom_edge_y_stick( next?: number ) {
			return this.widget().bottom_edge_y(next)
		}

		@ $mol_mem
		right_edge_x_stick( next?: number ) {
			return this.widget().right_edge_x(next)
		}

		@ $mol_mem
		top_edge_y_stick( next?: number ) {
			return this.widget().top_edge_y( next )
		}

		@ $mol_mem
		left_edge_x_stick( next?: number ) {
			return this.widget().left_edge_x( next )
		}

		hide_head_tools( next?: boolean ) {
			return this.widget().hide_head_tools( next )
		}

		@ $mol_mem
		title( next?: string ): string {
			return this.widget().title( next )
		}

		bind_view() {
			this.widget().view( this )
		}

		ref_str() {
			return this.widget().link().toString()!
		}

		z( next?: number ) {
			return this.widget().z( next )
		}

		delete() {
			this.delete_from_map()
		}

		@ $mol_mem
		opacity( next?: number ) {
			return this.widget().opacity( next )
		}

		@ $mol_mem
		locked( next?: boolean ) {
			return this.widget().locked(next)
		}

		locked_attr() {
			return this.map().interaction_locked() || this.locked()
		}

		opacity_str(): string {
			return this.opacity().toString()
		}

		outline(): readonly ( any )[] {
			if( this.Map_page().widget_tools_hidden() ) return []
			return super.outline()
		}

		edges(): readonly ( any )[] {
			if( !this.map().can_change() ) return []
			if( this.Map_page().widget_tools_hidden() ) return []
			// let edges = this.hide_head_tools() ? [
			// 	this.Top_edge(),
			// 	this.Left_edge(),
			// 	this.Bottom_edge(),
			// 	this.Right_edge(),
			// ] : super.edges()
			let edges = super.edges()
			if( this.selected() && this.Map_page().selected_links().length > 1 ) return []
			return this.selected() && !this.dragged() ? edges : []
		}

		toolbar(): readonly ( any )[] {
			if( !this.map().can_change() ) return []
			if( this.Map_page().widget_tools_hidden() ) return []
			if( this.hide_head_tools() ) return []
			if( this.Map_page().selected_links().length > 1 ) return []
			// Only the selected, stationary widget can show a toolbar. Reading
			// view_rect before this guard subscribes every idle widget to frames.
			if( !this.selected() || this.dragged() ) return []
			const viewport = this.Map_page().Pane().Back().view_rect()
			const widget = this.view_rect()
			if( viewport && widget && ( widget.right <= viewport.left || widget.left >= viewport.right ) ) return []
			return super.toolbar()
		}

		@ $mol_mem
		toolbar_transform() {
			return $shm_hitalama_map_float_toolbar_transform( this.zoom() )
		}

		toolbar_tools_max_width() {
			const viewport = this.Map_page().Pane().Back().view_rect()
			return viewport ? `${ Math.max( 1, viewport.width - 16 ) }px` : '100vw'
		}

		toolbar_tools_left() {
			const viewport = this.Map_page().Pane().Back().view_rect()
			const widget = this.view_rect()
			const tools = this.Tools().view_rect()
			if( !viewport || !widget || !tools ) return '0px'

			const margin = 8
			const width = Math.min( tools.width, viewport.width - margin * 2 )
			const left = Math.max( viewport.left + margin,
				Math.min( widget.left, viewport.right - margin - width ) )
			return `${ ( left - widget.left ) / this.zoom() }px`
		}

		link_url() {
			return this.widget().link_url().trim()
		}

		widget_link(): readonly ( any )[] {
			return this.link_url() ? super.widget_link() : []
		}

		widget_link_transform() {
			return `scale(${ 1 / this.zoom() })`
		}

		widget_link_pointerdown( event?: PointerEvent | null ) {
			event?.stopPropagation()
			return event ?? null
		}

		is_inner() {
			return Boolean( this.map().widget_container_actual( this.widget() ) )
		}

		some_container_dragged() {
			let container = this.map().widget_container_actual( this.widget() )
			while (container) {
				if( this.Map_page().Widget( container.link() ).Sub().transforming() ) return true
				container = this.map().widget_container_actual( container )
			}
			return false
		}

		some_selected_container_in_selectbox_drag() {
			if( !this.Map_page().selectbox_dragged() ) return false

			let container = this.map().widget_container_actual( this.widget() )
			while (container) {
				const container_link = container.link()
				if( this.Map_page().selected_links().some( link => link.str == container_link.str ) ) return true
				container = this.map().widget_container_actual( container )
			}
			return false
		}

		@ $mol_mem
		transition() {
			if( this.selected() && this.Map_page().selectbox_dragged() ) return 'none'
			if( this.some_selected_container_in_selectbox_drag() ) return 'none'
			if( this.some_container_dragged() ) return 'none'

			return ( this.transforming() ) ? 'none' : this.transition_smooth()
		}

		selected_attr() {
			return this.locked() ? false : this.selected()
		}

		dragged_attr() {
			return this.locked() ? false : this.dragged() || ( this.selected() && this.Map_page().selectbox_dragged() )
		}

		dragged(): ReturnType<ReturnType<$.$rise_resize[ "Drag_view" ]>[ "dragged" ]> {
			return this.Drag_view().dragged()
		}

		zoom_for_resizers() {
			return Math.min( 5, this.zoom() )
		}

		resizer_transform() {
			return $shm_hitalama_map_float_resizer_transform( this.zoom() )
		}

		resizer_line_x_transform() {
			return $shm_hitalama_map_float_resizer_line_x_transform( this.zoom() )
		}

		resizer_line_y_transform() {
			return $shm_hitalama_map_float_resizer_line_y_transform( this.zoom() )
		}

		@ $mol_mem
		has_scrollbar_y() {
			const el = this.Content_scroll().dom_node_actual()
			return el.scrollHeight > el.clientHeight
		}
		
		@ $mol_mem
		has_scrollbar_x() {
			const el = this.Content_scroll().dom_node_actual()
			return el.scrollWidth > el.clientWidth
		}

		hover_styled() {
			return this.Map_page().hover_styled( this.widget().link() )
		}

		pointerdown_last?: PointerEvent
		pointerdown( event: PointerEvent ) {
			this.pointerdown_last = event
		}

		content_pointerdown_last?: { time: number, x: number, y: number, button: number }
		content_pointerdown_doubled( event: PointerEvent ) {
			const time = performance.now()
			const last = this.content_pointerdown_last
			this.content_pointerdown_last = {
				time,
				x: event.clientX,
				y: event.clientY,
				button: event.button,
			}

			if( !last ) return false
			if( last.button !== event.button ) return false
			if( time - last.time > 500 ) return false

			const shift_x = event.clientX - last.x
			const shift_y = event.clientY - last.y
			return shift_x * shift_x + shift_y * shift_y < 25
		}

		content_double_click( event: PointerEvent ) {
		}

		skip_drag( el: Element ) {
			if( window.getComputedStyle(el).userSelect == 'text' ) return true
			
			return ['A','RISE_DRAG_VIEW','MOL_BUTTON_MINOR','MOL_BUTTON_MAJOR','MOL_DIMMER'].includes(el?.tagName)
				|| $shm_hitalama_is_input( el )
		}
		
		@ $mol_mem
		color( next?: string ) {
			return this.widget().color(next) ?? 'var(--mol_theme_text)'
		}

		@ $mol_mem
		font_size( next?: number ) {
			return this.widget().font_size(next)
		}

		@ $mol_mem
		font_size_px() {
			return this.font_size() + 'px'
		}

		@ $mol_mem
		font_family( next?: string ) {
			return this.widget().font_family(next)
		}

		@ $mol_mem
		auto_height( next?: boolean ) {
			return this.widget().auto_height(next)
		}

		fullscreen_attr() {
			return this.$.$mol_state_arg.value( 'fullscreen_widget' ) === this.widget().link().str
		}

		fullsize_height() {
			return this.fullscreen_attr() ? '100%' : ''
		}

		fullsize_min_height() {
			return this.auto_height() && !this.fullscreen_attr() ? '0' : '100%'
		}

		content_scroll_overflow() {
			return this.auto_height() ? 'hidden' : ''
		}

		@ $mol_mem
		drag_overlap_pointer_events() {
			return this.Map_page().Pane().drag_mode_enabled() ? 'auto' : 'none'
		}

		drag_overlap(): readonly ( any )[] {
			if( this.map().interaction_locked() ) return []
			return this.Map_page().Pane().drag_mode_enabled() ? super.drag_overlap() : []
		}

		@ $mol_action
		auto_height_calc() {
			const content = this.Fullsize_wrapper().dom_node_actual()
			const head = this.head_view().length
				? this.Widget_head().dom_node_actual().getBoundingClientRect().height / this.zoom()
				: 0
			return Math.ceil( content.scrollHeight + head )
		}

		height_px(): string {
			if( !this.auto_height() ) return super.height_px()

			this.$.$mol_state_time.now( 100 )

			const height = this.auto_height_calc()
			return ( height > 0 ? height : this.height() ) + 'px'
		}

		@ $mol_mem
		auto_height_fit() {
			if( !this.map().can_change() ) return null
			if( !this.auto_height() ) return null
			if( this.transforming() ) return null

			this.$.$mol_state_time.now( 100 ) //fix //todo

			const height = this.auto_height_calc()
			if( height > 0 && Math.abs( this.height() - height ) > 1 ) this.height( height )

			return null
		}

		color_with_opacity( color: string, opacity: number ): string {
			if( !color || color == 'transparent' ) return color

			opacity = Math.max( 0, Math.min( 1, opacity ) )
			const match = color.match( /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i )
			if( !match ) return color

			const [ , red, green, blue ] = match
			return `rgba(${ parseInt( red, 16 ) }, ${ parseInt( green, 16 ) }, ${ parseInt( blue, 16 ) }, ${ opacity })`
		}

		@ $mol_mem
		background_style(): string {
			return this.color_with_opacity(
				this.widget().background_color(),
				this.widget().background_opacity(),
			)
		}

		@ $mol_mem
		border_color_style(): string {
			if( this.widget().border_width() <= 0 ) return ''

			return this.color_with_opacity(
				this.widget().border_color(),
				this.widget().border_opacity(),
			)
		}

		@ $mol_mem
		border_width_px(): string {
			const width = Math.max( 0, this.widget().border_width() )
			if( width === 0 ) return ''

			return `${ width }px`
		}

		border_style(): string {
			if( this.widget().border_width() <= 0 ) return ''

			return this.widget().border_style()
		}

		on_drag_start( next?: any ) {
			$shm_hitalama_map_history.geometry_begin_many([ this.widget() ])
			super.on_drag_start( next )
			this.selected( true )
			return next ?? null
		}

		content_pointerdown( event: PointerEvent ) {
			if( this.map().interaction_locked() ) return
			if( !this.map().can_change() ) {
				this.selected( true )
				return
			}
			if( this.fullscreen_attr() ) return
			if( this.Content_scroll().dom_node_actual().matches( ':popover-open' ) ) return
			if( this.locked() ) {
				this.selected( true )
				return
			}

			const el = (event.target as any) as Element
			// Content touches belong to native scrolling and editing. Explicit drag
			// handles call drag_pointerdown directly and still move the widget.
			if( event.pointerType === 'touch' && !this.Map_page().Pane().drag_mode_enabled() ) {
				this.selected( true )
				return
			}

			const container = this.map().widget_container_actual( this.widget() )
			if( container ) {
				if( this.content_pointerdown_doubled( event ) ) {
					this.Map_page().selected_links( [ this.widget().link() ] )
					this.content_double_click( event )
					return
				}
				if( this.selected() ) {
					this.drag_pointerdown( event )
					return
				}
				this.Map_page().Widget( container.link() ).Sub().content_pointerdown( event )
				return
			}

			if( this.selected() && this.Map_page().selected_links().length > 1 ) {
				this.Map_page().Selectbox().pointerdown( event )
				return
			}
			
			if( this.skip_drag( el ) ) {
				if( !this.Map_page().Pane().drag_mode_enabled() ) {
					this.selected(true)
					return
				}
			}

			this.drag_pointerdown( event )
		}

		event_contextmenu( event: PointerEvent ) {
			if( $shm_hitalama_is_input( event?.target as Element ) ) {
				return
			}

			event.preventDefault()
			if( event.pointerType === 'touch' ) return

			if( !this.pointerdown_last
				|| $shm_hitalama_is_panning( this.pointerdown_last, event )
			) return
			
			this.on_contextmenu( event )
		}

		@ $mol_mem
		control_attr(): boolean {
			// return false
			const link = this.widget().controlled_widget()?.link()
				?? this.widget().settings_widget()?.link()
				?? this.widget().code_widget()?.link()
				?? this.widget().view_widget()?.link()
			return link ? this.Map_page().selected(link) : false
		}
		
	}

}
